import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Dialog, Typography } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { assignInspectorBulk, emailForTaskAssignment } from '../../graphql';
import { useMst } from '../../mobx-models/Root';
import { useRxDB } from '../../rxdb/useRxDB';
import { sentryCaptureException, useOnlineStatus } from '../../utilities';
import { BATCH_SIZE_REASSIGN } from '../../utilities/constants';
import { useNotification } from '../../utilities/useNotification';
import CustomButton from '../CustomButton/CustomButton';

const useStyles = makeStyles({
  mainGrid: {
    padding: '25px 0px 25px 0px',
  },
  mainText: {
    width: '100%',
    letterSpacing: '0.15px',
    color: 'rgba(255, 255, 255, 0.87)',
  },
  buttonPadding: {
    padding: '4px 8px',
  },
});

interface Props {
  open: boolean;
  onClose: () => void;
  isAssignToLead: boolean;
}

const AssignToMeDialog: React.FC<Props> = ({ open, onClose, isAssignToLead }) => {
  const { HandleGesture, CurrentUserSelected, SiteDiscipline } = useMst();
  const { db } = useRxDB();
  const currentInspectorId = CurrentUserSelected?.currentUser?.inspectorId;
  const currentClerkId = CurrentUserSelected?.currentUser?.clerkId;
  const { successNotification, errorNotification } = useNotification();
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);
  const online = useOnlineStatus();
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (HandleGesture.inspectionIds.length === 1)
      setMessage(`Inspection ID ${HandleGesture.inspectionIds[0]}`);
    else setMessage('Selected Inspection');
  }, [HandleGesture.inspectionIds, HandleGesture.inspectionIds.length]);

  const displayMessage = () => {
    return (
      <>
        {' '}
        {'Are you sure want to Assign the '} <b>{message}</b> {' to yourself ?'}{' '}
      </>
    );
  };

  const handleAssignToMe = async () => {
    setIsSaving(true);
    const pkAssetInspectionTask = 0;
    const isReviewedByLI = false;
    const isReviewedByFI = false;
    const commentInspectorLead = '';
    const commentInspectorField = '';
    const leadInspectorId = 0;
    const workPackageId = CurrentUserSelected.workPackageId;
    const fieldInspectorId = currentInspectorId;
    const clerkId = currentClerkId;
    const siteId = SiteDiscipline.siteId;
    const disciplineId = SiteDiscipline.disciplineId;
    const isActive = true;
    const taskStatus = 5;
    const recommendations = '';
    const taskDescription = '';
    const dateCompleted = null;
    const isPerformedByFI = true;
    const inspectionIds = HandleGesture.inspectionIds.toJSON();
    const isAssignmentByFI = !isAssignToLead;
    const listOfInspections: { inspectionId: number }[] = inspectionIds.map(value => {
      return { inspectionId: value };
    });
    let position = 0;
    let results: unknown[] = [];

    while (position < inspectionIds.length) {
      const itemsForBatch = inspectionIds.slice(position, position + BATCH_SIZE_REASSIGN);
      results = [
        ...results,
        ...(await Promise.all(
          itemsForBatch.map(async inspectionId => {
            try {
              const result = await assignInspectorBulk(
                pkAssetInspectionTask,
                workPackageId,
                inspectionId,
                leadInspectorId,
                fieldInspectorId || 0,
                clerkId || 0,
                taskDescription,
                isReviewedByLI,
                isReviewedByFI,
                commentInspectorLead,
                commentInspectorField,
                isActive,
                siteId,
                disciplineId,
                uuidv4(),
                taskStatus,
                recommendations,
                dateCompleted,
                isPerformedByFI,
                inspectionIds.length === 1 ? false : true,
              );
              if (result.errors) {
                throw new Error(`Error in assigning Inspection Id ${inspectionId}`);
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error(error);
              // console logs will be removed once sentry implementation is done
              sentryCaptureException(
                error,
                '~ file: AssignToMeDialog.tsx ~ line 120 ~ handleAssignToMe ~',
                'bulkOperation',
                JSON.stringify({ inspectionId, leadInspectorId, fieldInspectorId }),
              );
            }
          }),
        )),
      ];
      position += BATCH_SIZE_REASSIGN;
    }

    try {
      if (results.length === inspectionIds.length) {
        if (inspectionIds.length > 1) {
          await emailForTaskAssignment(
            workPackageId,
            fieldInspectorId || 0,
            leadInspectorId,
            clerkId || 0,
            listOfInspections,
            isAssignmentByFI,
          );
        }
        successNotification(
          `${
            HandleGesture.inspectionIds.length === 1
              ? `Inspection ID ${HandleGesture.inspectionIds[0]}`
              : `Inspections`
          } Assigned to You Successfully!`,
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Bulk email trigger failed ', error);
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: AssignToMeDialog.tsx ~ line 160 ~ emailForTaskAssignment ~',
      );
      errorNotification('Bulk Assigned Failed. Please try agaon later');
    } finally {
      db?.startReplication();
      setIsSaving(false);
      onClose();
      HandleGesture.resetEverything();
      HandleGesture.setIsCompleted(true);
      HandleGesture.setRunOnInspectionSubscription(true);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      open={open}
      titleText="Assign To Me"
      backButton={false}
      dialogActions={
        online ? (
          <Grid
            container
            justifyContent="space-between"
            spacing={2}
            className={classes.buttonPadding}
          >
            <Grid item>
              <Button onClick={onClose} variant="outlined" color="primary" noMinHeight>
                Cancel
              </Button>
            </Grid>
            <CustomButton
              isLoading={isSaving}
              onClose={handleAssignToMe}
              buttonText="Yes, Assign To Me"
            />
          </Grid>
        ) : (
          <></>
        )
      }
      maxWidth="xs"
      noMinHeight
    >
      <Grid container className={classes.mainGrid}>
        <Grid item container xs={12} alignItems="center">
          <Typography variant="h6" className={classes.mainText}>
            {online ? displayMessage() : `User Need To Connected With Internet To Delete Task`}
          </Typography>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default observer(AssignToMeDialog);
